import React from 'react';
import { connect } from 'react-redux';
import { View } from 'react-native-web';

import Janitor from '../../../../modules/Janitor';
import {
  loadPreviousAppointments,
  ProPromotion,
  UserAppointment,
} from '../../../../api/Users/Appointments';
import formatDiscount from '../../../../modules/formatters/formatPromotionDiscount';
import ABTest from '../../../../modules/abTest';
import FeatureFlags from '../../../../modules/FeatureFlags';
import analytics from '../../../../modules/analytics';
import {
  DEFAULT_GROUP_NAME,
  SORTED_SERVICE_TEST_FLAG,
  SORTED_SERVICE_TEST_PERCENT,
} from './constants';

import withStore from '../../../withStore';

import ServiceList from './ServiceList';

import { CLIENT_BOOKING_SERVICE_LIST_PREVIOUS_APPT_TEST_KEY, CLIENT_BOOKING_SERVICE_LIST_PREVIOUS_APPT_TEST_PERCENT } from '../constants';

import { selectors, isViewingEditableProfile as checkViewingEditableProfile } from '../../../../store/CurrentUser.model';
import { ProviderServiceGroup } from '../../../../api/Providers/ServiceGroups';
import { PublicProviderProfile } from '../../../../modules/Api.types';
import { IRootDispatch } from '../../../../store/models';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { ProviderService } from '../../../../api/Providers/Services';

const DEFAULT_VISIBLE_PROMOTIONS = 3;

export type ServiceListContainerProps = {
  /**
     * Changes the service list appearance
     * default:   All services, top services, last booked services, etc.
     * notifyMe:  Shows only the service list, grouped in sections,
     *            with a simplified service item
     */
  appearance?: 'default' | 'notifyMe';
  /** The list of services to display as popular */
  popularServices?: ProviderService[];
  /** True if the current user is not logged in */
  isAnon?: boolean;
  /** True if we are able to edit services */
  isEditMode?: boolean;
  /** isOwnProfile * */
  isOwnProfile?: boolean;
  /** True if booking should be disabled */
  disableBooking?: boolean;
  /** The provider for which to render services, if any */
  provider?: PublicProviderProfile | ClientProviderProfile;
  /** Function to hide services from the list if they fit a criteria */
  serviceFilter?: (svc: ProviderService) => boolean;
  /** Optional click handler for the item row; hides the action button */
  onItemPress?: () => void;
  /** The user ID of the current user */
  userId?: number;
  /** True if viewing a profile we can edit */
  isViewingEditableProfile?: boolean;
  /** Given route and params, navigate to the desired state */
  linkTo?: (route: string, params: Record<string, any>, notify?: boolean) => any;
  /** Given a profile id and type, load the pertaining service group data */
  loadServiceGroups?: (providerId: any, opts: any, loadPromotions: any) => any;
  /** To show or not to show promotions */
  showPromotions?: boolean;
  /** Function that returns a component to show next to each item in the ServiceList */
  renderRight?: () => React.ReactNode;
  /** onBlurbStatsCounted - a function called when the blurbs are parsed and defaulted */
  onBlurbStatsCounted?: (payload: {
    emptyBlurbCount: number;
    totalBlurbCount: number;
    defaultedBlurbCount: number;
  }) => void;
  /** The total number of reviews for the current profile, -1 means not yet known */
  reviewCount?: number;
  /** Indicates whether we're currently fetching service group data */
  loadingServiceGroups?: boolean;
  /** True if we can load more data */
  morePagesToLoad?: boolean;
  /** Metadata related to the service group request */
  paging?: { next: string };
  /** List of all service groups for current profile */
  serviceGroups?: ProviderServiceGroup[];
  /** Map of all services loaded for current profile */
  serviceMap?: Record<number, ProviderService>;
  highlightedServiceId?: number;
  /** True if in see all services test (ATL-3806), otherwise false */
  isInSeeAllServicesTest?: boolean;
  /**
  * Max services to show,
  */
  maxServiceCount?: number;
  /**
  * Show all services
  */
  onShowAllServices?: () => void;
  /**
  * Mapping from service ID to array of applicable promotions
  */
  promoServiceMap?: Record<number, ProPromotion[]>;
  /**
  * Array of promoted services
  */
  promotedServices?: ProviderService[];
  popularServiceRankingMap?: Record<number, number>;
  /** True if we're showing recommendations at the bottom and will truncate the service list */
  hasProviderRecommendationData?: boolean;
  isMobile: boolean;
};

type ServiceListContainerState = {
  showAllPromotions: boolean;
  serviceGroupPage: number;
  isInSortedServicesTest: boolean;
  previousAppointment: UserAppointment | null;
};

export class ServiceListContainer extends React.Component<
ServiceListContainerProps,
ServiceListContainerState
> {
  static defaultProps = {
    appearance: 'default',
    popularServices: [],
    isAnon: true,
    isEditMode: false,
    isOwnProfile: false,
    disableBooking: false,
    provider: null,
    serviceFilter: null,
    onItemPress: null,
    userId: undefined,
    linkTo: () => { },
    loadServiceGroups: () => { },
    showPromotions: true,
    renderRight: null,
    onBlurbStatsCounted: () => {},
    isViewingEditableProfile: false,
    reviewCount: -1,
    loadingServiceGroups: false,
    morePagesToLoad: false,
    paging: {},
    serviceGroups: [],
    serviceMap: {},
    highlightedServiceId: undefined,
    isInSeeAllServicesTest: false,
    maxServiceCount: null,
    onShowAllServices: () => {},
    promoServiceMap: {},
    promotedServices: [],
    popularServiceRankingMap: {},
  };

  state = {
    showAllPromotions: false,
    serviceGroupPage: -1,
    /** The previous appointment between the current user and this pro, if any */
    previousAppointment: null,
    /** Is in test that filters highlighted service to top of list */
    isInSortedServicesTest: false,
  };

  profile = null;

  janitor = new Janitor();

  componentDidMount() {
    const {
      provider,
    } = this.props;

    this.profile = provider;
    this.loadPreviousAppointment();

    this.janitor.addPromise(
      this.assignSortedServicesTest(),
    ).then(() => this.loadServices(0));
  }

  componentDidUpdate(prevProps) {
    const hasNoReviews = prevProps.reviewCount === -1 && this.props.reviewCount === 0;
    const isServicesLoaded = prevProps.loadingServiceGroups && !this.props.loadingServiceGroups;

    if (hasNoReviews) {
      analytics.track('client_proprofile_no_reviews');
    }
    if (isServicesLoaded) {
      this.handleServicesLoaded();
    }
  }

  componentWillUnmount() {
    this.janitor.cleanup();
  }

  /**
   * Sets state variable when in test and calls tracking.
   * @param {void}
   * @returns {void}
   */
  async assignSortedServicesTest() {
    const {
      provider,
      highlightedServiceId,
    } = this.props;

    if (!highlightedServiceId) {
      return;
    }

    const testEnabled = await this.janitor.addPromise(
      ABTest.isInFeatureTest(SORTED_SERVICE_TEST_FLAG, SORTED_SERVICE_TEST_PERCENT),
    );
    this.setState({ isInSortedServicesTest: testEnabled });

    if (testEnabled && highlightedServiceId) {
      analytics.track('client_proprofile_sorted_services_viewed', {
        provider_id: provider.id,
        highlighted_service_id: highlightedServiceId,
      });
    }
  }

  /**
   * Loads details about the previous appointment booked by this client.
   */
  loadPreviousAppointment() {
    const { userId, isEditMode } = this.props;

    if (userId && !isEditMode) {
      this.janitor.addPromise(
        FeatureFlags.isEnabled(CLIENT_BOOKING_SERVICE_LIST_PREVIOUS_APPT_TEST_KEY),
      )
        .then(enabled => {
          const isInTest = enabled && ABTest.isInTest(
            CLIENT_BOOKING_SERVICE_LIST_PREVIOUS_APPT_TEST_KEY,
            CLIENT_BOOKING_SERVICE_LIST_PREVIOUS_APPT_TEST_PERCENT,
          );

          if (isInTest) {
            return loadPreviousAppointments(userId, {
              size: 1,
              provider_id: this.profile.id,
            });
          }

          return {
            allCount: 0,
            results: [],
          };
        })
        .then(({ results: prevAppts }) => {
          if (prevAppts.length) {
            this.setState({ previousAppointment: prevAppts[0] });
          } else {
            this.setState({ previousAppointment: null });
          }
        });
    }
  }

  /**
   * Loads a page of service groups, using the page provided if loading groups for a salon. Service
   * groups are merged into relevant state variables.
   * @param {Number} page The page to load
   */
  async loadServices(page) {
    const {
      loadServiceGroups, showPromotions, isOwnProfile, isEditMode,
    } = this.props;
    const { id: profileId } = this.profile;
    // TODO(roger): As explained in ENG-5619, fix this bug and re-enable pagination.
    const opts: { size?: 'all', service_filter?: 'all' } = {
      size: 'all',
    };

    if (isOwnProfile && isEditMode) {
      opts.service_filter = 'all';
    }
    await loadServiceGroups(profileId, opts, showPromotions);
    this.setState({ serviceGroupPage: page });
  }

  handleServicesLoaded() {
    const { onBlurbStatsCounted, serviceGroups } = this.props;

    let emptyBlurbCount = 0;
    let totalBlurbCount = 0;
    let defaultedBlurbCount = 0;

    // TODO(BD): Need to only calculate analytics for newly added blurbs from paging
    serviceGroups.forEach(group => {
      group.services.forEach(({ blurb, isBlurbDefaulted }) => {
        totalBlurbCount += 1;
        if (blurb === '') {
          emptyBlurbCount += 1;
        } else if (isBlurbDefaulted) {
          defaultedBlurbCount += 1;
        }
      });
    });

    onBlurbStatsCounted({
      emptyBlurbCount,
      totalBlurbCount,
      defaultedBlurbCount,
    });
  }

  /**
   * Moves the highlighted service to the top of the services list.
   * @param {void}
   * @returns {ServiceGroup[]}
   */
  pushHighlightedGroupToTop() {
    const {
      highlightedServiceId,
      serviceGroups,
    } = this.props;
    const { isInSortedServicesTest } = this.state;

    if (!isInSortedServicesTest || !highlightedServiceId || !serviceGroups.length) {
      return null;
    }
    // find group and service index to move to the front of the list
    let serviceIndex = -1;
    const groupIndex = serviceGroups?.findIndex(
      group => {
        serviceIndex = group.services?.findIndex(y => y.id === highlightedServiceId);
        return serviceIndex >= 0;
      },
    );
    const orderedGroups = [...serviceGroups];

    if (serviceIndex >= 0 && groupIndex >= 0) {
      // move group containing highlighted service to fron of list
      const [group] = orderedGroups.splice(groupIndex, 1);
      orderedGroups.unshift(group);
      // move highlighted service to front of services list within the group
      const [service] = orderedGroups[0].services.splice(serviceIndex, 1);
      orderedGroups[0].services.unshift(service);
    }

    return orderedGroups;
  }

  /**
   * Formats ServiceGroup data to be consumed by SectionList compnent.
   * @param {void}
   * @returns {SectionListData[]}
   */
  formatServiceSections() {
    const { serviceGroups } = this.props;
    const sortedGroups = this.pushHighlightedGroupToTop();

    const groups = sortedGroups || serviceGroups;
    let overallIndex = 0;
    // create normal service sections, separated by service category, for the provider
    const serviceSections = groups
      .map((serviceGroup, index) => {
        const startingOverallIndex = overallIndex;
        const { name } = serviceGroup;

        overallIndex += serviceGroup.services.length;
        return {
          index: index + 1,
          startingOverallIndex,
          data: serviceGroup.services,
          name: name || DEFAULT_GROUP_NAME,
        };
      })
      // we filter after building the sections so that the section indices are consistent with DB
      // state, so that the resulting tracking properties match up with the right records
      .filter(section => section.data.length);

    return serviceSections;
  }

  /**
   * Generates the `sections` supplied to the `SectionList`
   * @returns {Array<Object>} An array of objects which satisfy the ServiceGroupPropType
   */
  createSections() {
    const {
      serviceMap,
      promotedServices,
      promoServiceMap,
    } = this.props;
    const {
      showAllPromotions,
      previousAppointment,
    } = this.state;
    let appointmentSection = [];
    let promotionSection = [];
    let promotionData = promotedServices;

    // creation section to contain the previously booked appointment with the provider
    if (previousAppointment) {
      const appt = previousAppointment.time_blocks
      // Find the services associated with each time block
        .map(timeBlock => serviceMap[timeBlock.service])
        // Get rid of any services that may have been deleted. Note that this is not sifting through
        // *all* services, just those associated with the previous appointment, which is generally a
        // small number
        .filter(service => !!service)
        // Combine the filtered set of services into a single `ServicePropType`
        .reduce((acc, curr) => ({
          idList: [
            ...acc.idList,
            curr.id,
          ],
          name: acc.name ? `${acc.name}, ${curr.name}` : curr.name,
          cost: acc.cost + Number(curr.cost),
          total_duration_minutes: acc.total_duration_minutes + curr.total_duration_minutes,
          cost_and_up: acc.cost_and_up || curr.cost_and_up,
          is_online_bookable: acc.is_online_bookable && curr.is_online_bookable,
          isPreviousBooking: true,
        }), {
          is_online_bookable: true,
          cost: 0,
          total_duration_minutes: 0,
          name: '',
          idList: [],
        });

      // this property is added on first pass of `reduce`, so it will only be true if there was a
      // previous appointment with at least one service
      if (appt.isPreviousBooking) {
        appointmentSection = [
          {
            index: -1,
            startingOverallIndex: -1,
            name: 'Last Appointment Booked',
            data: [appt],
          },
        ];
      }
    }

    const serviceSections = this.formatServiceSections();

    if (!showAllPromotions) {
      promotionData = promotedServices.slice(0, DEFAULT_VISIBLE_PROMOTIONS);
    }

    promotionData = promotionData.map(promotedService => ({
      ...serviceMap[promotedService.id],
      promo: promoServiceMap[promotedService.id]?.[0],
    }));

    // create promotion section for the provider
    if (promotionData.length) {
      promotionSection = [
        {
          showAllPromotions,
          name: 'Promotions',
          data: promotionData,
          startingOverallIndex: 0,
          index: 0,
        },
      ];
    }

    return [
      ...appointmentSection,
      ...promotionSection,
      ...serviceSections,
    ];
  }

  /**
   * Handles reaching the end of the service list, which for salons, triggers loading of the next
   * page of results.
   */
  handleEndReached = () => {
    const { serviceGroupPage } = this.state;
    const { loadingServiceGroups, morePagesToLoad } = this.props;

    if (!loadingServiceGroups && morePagesToLoad) {
      this.loadServices(serviceGroupPage + 1);
    }
  };

  /**
   * Handles the toggling of promotions
   */
  handlePromotionsToggle = () => {
    const { showAllPromotions } = this.state;

    this.setState({ showAllPromotions: !showAllPromotions });
  };

  /**
   * Given a service item, determines the props for the component.
   */
  getServiceItemProps = service => {
    const { promoServiceMap } = this.props;
    const props: Record<string, any> = {};

    let promotion;
    let promotionProp = null;

    // we are rendering a promotion
    if (service.promo) {
      promotion = service.promo;
      promotionProp = {
        ...promotion,
        formattedDiscount: formatDiscount(promotion),
        startTime: promotion.book_start_time, // this is a moment
        endTime: promotion.book_end_time, // this is a moment
      };
    } else {
      // we are rendering a service, potentially with a promotion
      promotion = promoServiceMap[service.id]?.[0];
    }

    props.formattedDiscount = promotion && formatDiscount(promotion);
    props.promotion = promotionProp;

    if (service.isPreviousBooking) {
      props.bookText = <span>Book&nbsp;Again</span>;
    }

    return props;
  };

  render() {
    const serviceGroups = this.createSections();
    const {
      showAllPromotions,
      isInSortedServicesTest,
    } = this.state;

    const {
      promotedServices,
      morePagesToLoad,
      highlightedServiceId,
      isInSeeAllServicesTest,
      popularServiceRankingMap,
      isMobile,
      hasProviderRecommendationData,
    } = this.props;

    if (serviceGroups.length === 0) {
      return null;
    }

    return (
      <View nativeID="services-container">
        <ServiceList
          {...this.props}
          onEndReached={this.handleEndReached}
          onPromotionsToggle={this.handlePromotionsToggle}
          serviceGroups={serviceGroups}
          morePagesToLoad={morePagesToLoad}
          showAllPromotions={showAllPromotions}
          promotionCount={promotedServices.length}
          serviceItemProps={this.getServiceItemProps}
          highlightedServiceId={highlightedServiceId}
          isInSortedServicesTest={isInSortedServicesTest}
          isInSeeAllServicesTest={isInSeeAllServicesTest}
          popularServiceRankingMap={popularServiceRankingMap}
          isMobile={isMobile}
          hasProviderRecommendationData={hasProviderRecommendationData}
        />
      </View>
    );
  }
}

const filterServiceGroups = (
  serviceGroups,
  serviceFilter,
  isInSeeAllServicesTest,
  popularServices,
) => {
  let filteredServiceGroups = [...serviceGroups];

  if (serviceGroups?.length && serviceFilter) {
    filteredServiceGroups = serviceGroups.map(group => ({
      ...group,
      services: group.services.filter(service => serviceFilter(service)),
    }));
  }

  if (isInSeeAllServicesTest) {
    filteredServiceGroups.unshift({
      name: 'Popular Services',
      services: popularServices,
    });
  }

  return filteredServiceGroups;
};

const mapStateToProps = (rootState, {
  provider,
  serviceFilter,
  isInSeeAllServicesTest,
  popularServices,
}) => {
  const profile = provider; // Determine type of profile
  const {
    providerReviews: reviewState,
    profileServiceGroups: serviceGroupState,
  } = rootState; // Convenience refs to state

  const serviceGroupStateForPro = serviceGroupState?.[profile.id];

  return {
    currentRouteName: rootState.route.name,
    isViewingEditableProfile: checkViewingEditableProfile(profile.id, rootState),
    loadingServiceGroups: serviceGroupState.loading,
    morePagesToLoad: profile && !!serviceGroupStateForPro?.paging?.next,
    paging: serviceGroupStateForPro?.paging,
    reviewCount: reviewState?.providerMap?.[profile.id]?.total,
    serviceGroups: filterServiceGroups(
      serviceGroupStateForPro?.groups || [],
      serviceFilter,
      isInSeeAllServicesTest,
      popularServices,
    ),
    serviceMap: serviceGroupStateForPro?.servicesById,
    promotedServices: serviceGroupStateForPro?.promotedServices,
    promoServiceMap: serviceGroupStateForPro?.promotionsByServiceId,
    userId: selectors.getId(rootState),
    isAnon: !selectors.isLoggedIn(rootState),
  };
};

const mapDispatchToProps = (dispatch: IRootDispatch, {
  isViewingEditableProfile,
}) => ({
  linkTo: (route, params, notify = true) => dispatch.route.go({
    route,
    params,
    notify,
  }),
  loadServiceGroups: (providerId, opts, loadPromotions) => (
    dispatch.profileServiceGroups.loadProvider({
      providerId,
      opts,
      loadPromotions,
      isViewingEditableProfile,
    })
  ),
});

// This with store wrapping should be removed but FillScheduleGaps needs to be refactored first
// @ts-expect-error Functionally, this was working fine before converting to TS
export default withStore(connect(mapStateToProps, mapDispatchToProps)(ServiceListContainer));
